import React, { useContext } from "react";
import { NextSeo, SocialProfileJsonLd } from "next-seo";
import { DEFAULT_SPORT } from "../constants";
import styles from "../styles/index.scss";
import LiveGameBar from "../components/LiveGameBar";
import { prefetchLiveEvents } from "../components/LiveGameBar/liveEvents";
import Hero from "../components/Hero";
import { LatestArticles } from "../components/ArticleGrid";
import FeaturedPages from "../components/FeaturedPages";
import OffersCarousel from "../components/OffersCarousel";
import GamesIndex, { getGameIndexVars, useGamesIndexData } from "../components/GamesIndex";
import FeaturedTrends from "src/components/FeaturedTrends";
import Surveys from "../components/Surveys";
import { SubscriptionContext } from "src/services/SubscriptionService";
import { GetStaticProps, InferGetStaticPropsType } from "next";
import { useIndexArticles } from "src/services/FeaturedContent";
import { addApolloState, initializeApollo } from "src/lib/apollo-client";
import { INDEX_EVENTS, INFINITE_ARTICLES_QUERY, LEAGUE_CONFIG_QUERY } from "src/gql/queries";
import YouTubePlaylistEmbed from "src/components/YouTubePlaylistEmbed";
import { renderArticleRichText } from "src/services/PrismicService";

export const getStaticProps: GetStaticProps<{ seoContent: any }> = async ({ previewData }) => {
  const apolloClient = initializeApollo();
  let context: any = {};
  if (previewData) {
    context.headers = {
      "Prismic-ref": (previewData as { ref: string })?.ref,
    };
  }
  // Load query results into apollo cache for hydration
  const { data } = await apolloClient.query({
    query: LEAGUE_CONFIG_QUERY,
    variables: {
      league: "none",
      includeSurveyResponses: false,
    },
    context,
  });
  const seoContent = data.prismicAllLeague_configs.edges[0].node.index_seo_content;
  await apolloClient.query({
    query: INFINITE_ARTICLES_QUERY,
    variables: {
      first: 9,
      where: {
        hidden: false,
        league: undefined,
      },
    },
    context,
  });
  await apolloClient.query({
    query: INDEX_EVENTS,
    variables: await getGameIndexVars(),
    context,
  });
  await prefetchLiveEvents(apolloClient, DEFAULT_SPORT);
  return addApolloState(apolloClient, {
    props: { seoContent },
    revalidate: 10 * 60, // 10 mins
  });
};

const title = "BetQL - Trending Sports Betting Tips & Daily Expert Tips";
const HOMEPAGE_YOUTUBE_PLAYLIST_ID = "PLUj5k6ZarEt8qr-ptx5gC8KiDRJZ-ptw6";

export default function Index({ seoContent }: InferGetStaticPropsType<typeof getStaticProps>) {
  const ctx = useContext(SubscriptionContext);
  const articles = useIndexArticles({});
  const games = useGamesIndexData();
  return (
    <React.Fragment>
      <NextSeo
        title={title}
        openGraph={{
          url: "https://www.betql.co",
          title,
        }}
      />
      <SocialProfileJsonLd
        type="Organization"
        name="BetQL"
        url="https://betql.co"
        sameAs={[
          "https://www.facebook.com/betql/",
          "https://www.instagram.com/betqlapp",
          "https://www.linkedin.com/company/rotoql",
          "https://twitter.com/betqlapp",
        ]}
      />
      <LiveGameBar sport={DEFAULT_SPORT} />
      <div id="main">
        <Hero />
        <FeaturedTrends isHomepage />
        <section className={`home-page ${ctx.signedIn ? "signed-in" : ""}`}>
          <FeaturedPages />
          <YouTubePlaylistEmbed playlistId={HOMEPAGE_YOUTUBE_PLAYLIST_ID} />
          <LatestArticles articles={articles} />
          <OffersCarousel headingStyle={{ fontSize: "22px", fontWeight: 900 }} />
          <GamesIndex games={games} />
          <Surveys header={<h2 className="heading">Latest Polls</h2>} />
          {seoContent && renderArticleRichText(seoContent)}
          <style jsx global>
            {styles}
          </style>
        </section>
      </div>
    </React.Fragment>
  );
}
